import React, { useCallback, useMemo } from 'react'
import { selectCurrentProject } from '../../../redux/application-slice'
import { Revision } from '../../../shared/interfaces/project/document/revision/revision.interface'
import CommentTableCard from './comment-table-card'
import CommentLoadingCard from './comment-loading-card'
import CommentTableHeader from './comment-table-header'
import { Tooltip } from 'react-tooltip'
import DuplicatorStatus from '../../duplicator/duplicator-status'
import {
  selectCommentWorkflowFilterState,
  selectCommentWorkflowFilterStateQueryParams,
} from '../../../redux/workflow-slice'
import { useSelector } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetRevisionsQuery } from '../../../redux/api/api-revisions-slice'
import { useGetDocumentBlacklinesQuery } from '../../../redux/api-slice'
import { DocumentBlackline } from '../../../shared/interfaces/project/document/blackline/document-blackline.interface'
import BlacklineCommentTableCard from './blackline-comment-table-card'
function CommentTablePage() {
  const currentProject = useSelector(selectCurrentProject)
  const revisionQueryParams = useSelector(
    selectCommentWorkflowFilterStateQueryParams
  )
  const commentWorkflowFilterState = useSelector(
    selectCommentWorkflowFilterState
  )
  const { data: revisionsData, isLoading: revisionsLoading } =
    useGetRevisionsQuery(
      revisionQueryParams?.documentIds?.length || revisionQueryParams?.projectId
        ? revisionQueryParams
        : skipToken
    )
  const { data: documentBlacklineSegments } = useGetDocumentBlacklinesQuery(
    {
      documentIDs: revisionQueryParams?.documentIds,
      projectID: !revisionQueryParams?.documentIds?.length
        ? revisionQueryParams?.projectId
        : undefined,
      isCommentsOnly: true,
    },
    {
      skip:
        !revisionQueryParams?.documentIds?.length &&
        !revisionQueryParams?.projectId,
    }
  )

  const filterRevisionsByQuery = useCallback(
    (revisions: Revision[]) => {
      if (!commentWorkflowFilterState.searchQuery) {
        return revisions
      }
      return revisions.filter(
        (r) =>
          r.document_title
            ?.toLowerCase()
            .includes(commentWorkflowFilterState.searchQuery.toLowerCase()) ||
          r.original_text
            ?.toLowerCase()
            .includes(commentWorkflowFilterState.searchQuery.toLowerCase()) ||
          r.v2_text
            ?.toLowerCase()
            .includes(commentWorkflowFilterState.searchQuery.toLowerCase()) ||
          r.document_title
            ?.toLowerCase()
            .includes(commentWorkflowFilterState.searchQuery.toLowerCase()) ||
          r.revised_text
            ?.toLowerCase()
            .includes(commentWorkflowFilterState.searchQuery.toLowerCase()) ||
          r.section_reference
            ?.toLowerCase()
            .includes(commentWorkflowFilterState.searchQuery.toLowerCase()) ||
          r.comments?.some(
            (c) =>
              c?.comment
                ?.toLowerCase()
                .includes(
                  commentWorkflowFilterState.searchQuery.toLowerCase()
                ) ||
              c?.user?.first_name
                ?.toLowerCase()
                .includes(
                  commentWorkflowFilterState.searchQuery.toLowerCase()
                ) ||
              c?.user?.last_name
                ?.toLowerCase()
                .includes(commentWorkflowFilterState.searchQuery.toLowerCase())
          )
      )
    },
    [commentWorkflowFilterState.searchQuery]
  )

  const filterRevisionsByStatus = useCallback(
    (revisions: Revision[]) => {
      if (!commentWorkflowFilterState.statusFilter.length) {
        return revisions
      }
      return revisions.filter((r) =>
        commentWorkflowFilterState.statusFilter.includes(r.revision_status)
      )
    },
    [commentWorkflowFilterState.statusFilter]
  )

  const filterRevisionsByAuthors = useCallback(
    (revisions: Revision[]) => {
      if (!commentWorkflowFilterState.authorsFilter.length) {
        return revisions
      }
      return revisions.filter((r) =>
        commentWorkflowFilterState.authorsFilter.some(
          ({ id }) => r.user_created?.id === id
        )
      )
    },
    [commentWorkflowFilterState.authorsFilter]
  )

  const filterRevisionsByLatestVersion = useCallback(
    (revisions: Revision[]) => {
      return revisions.filter(
        (r) => !revisions.some((rev) => rev.v1_revision === r.id)
      )
    },
    []
  )

  const revisionsToDisplay: Revision[] = useMemo(() => {
    if (!revisionsData) {
      return []
    }

    let revisions: Revision[] = []

    revisions = revisionsData
    revisions = filterRevisionsByLatestVersion(revisions)
    revisions = filterRevisionsByStatus(revisions)
    revisions = filterRevisionsByAuthors(revisions)
    revisions = filterRevisionsByQuery(revisions)
    return revisions
  }, [
    revisionsData,
    filterRevisionsByLatestVersion,
    filterRevisionsByStatus,
    filterRevisionsByAuthors,
    filterRevisionsByQuery,
  ])

  const commentsToDisplay = useMemo(() => {
    let components: {
      page: number
      document: number
      component: React.ReactNode
    }[] = []
    if (revisionsLoading || !currentProject) {
      return []
    }
    components = revisionsToDisplay?.map((revision) => {
      return {
        page: revision.page ?? 0,
        document: revision.document ?? 0,
        component: (
          <CommentTableCard
            key={`comment_card_${revision.id}`}
            revision={revision}
          />
        ),
      }
    })
    if (documentBlacklineSegments) {
      components = components.concat(
        documentBlacklineSegments.map((segment) => {
          return {
            page: segment.page,
            document: (segment.document_blackline as DocumentBlackline).document
              ?.id,
            component: (
              <BlacklineCommentTableCard
                key={`blackline_comment_${segment.id}`}
                segment={segment}
              />
            ),
          }
        })
      )
    }
    return components
      .sort((a, b) => {
        if (a.document !== b.document) {
          return a.document - b.document
        }
        return a.page - b.page
      })
      .map((component) => component.component)
  }, [
    revisionsLoading,
    currentProject,
    revisionsToDisplay,
    documentBlacklineSegments,
  ])

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-none">
        <CommentTableHeader revisionsData={revisionsData ?? []} />
        <DuplicatorStatus revisionParams={revisionQueryParams} />
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="space-y-2 p-2">
          {!revisionsLoading && currentProject ? (
            commentsToDisplay.length > 0 ? (
              commentsToDisplay
            ) : (
              <div className="text-center text-sm text-gray-700">
                No comments to show
              </div>
            )
          ) : (
            <div>
              {Array(6)
                .fill(0)
                .map((_, index) => index)
                .map((loadingCard) => (
                  <CommentLoadingCard
                    key={`loading_commment_card_${loadingCard}`}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
      <Tooltip
        id={'comment-badge-tooltip'}
        style={{ zIndex: 100, fontSize: '0.75rem' }}
        className="text-xs"
      />
    </div>
  )
}

export default CommentTablePage
